/**
 * 图片压缩工具
 * 用于在上传前压缩图片，减小文件体积
 */

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  outputFormat?: 'jpeg' | 'png' | 'webp';
}

/**
 * 压缩单张图片
 * @param file 原始图片文件
 * @param options 压缩选项
 * @returns 压缩后的 base64 字符串
 */
export const compressImage = async (
  file: File,
  options: CompressOptions = {}
): Promise<string> => {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.85,
    outputFormat = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // 计算缩放比例
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建 Canvas 上下文'));
          return;
        }

        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为 base64
        const dataUrl = canvas.toDataURL(`image/${outputFormat}`, quality);
        resolve(dataUrl);
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * 压缩单张图片并返回 File 对象
 * @param file 原始图片文件
 * @param options 压缩选项
 * @returns 压缩后的 File 对象
 */
export const compressImageToFile = async (
  file: File,
  options: CompressOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.85,
    outputFormat = 'jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // 计算缩放比例
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建 Canvas 上下文'));
          return;
        }

        // 绘制压缩后的图片
        ctx.drawImage(img, 0, 0, width, height);

        // 转换为 Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('图片压缩失败'));
              return;
            }

            // 生成新的文件名
            const fileName = file.name.replace(/\.[^/.]+$/, '') + `.${outputFormat}`;

            // 创建新的 File 对象
            const compressedFile = new File([blob], fileName, {
              type: `image/${outputFormat}`,
              lastModified: Date.now()
            });

            resolve(compressedFile);
          },
          `image/${outputFormat}`,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * 批量压缩图片
 * @param files 图片文件数组
 * @param options 压缩选项
 * @returns 压缩后的 base64 字符串数组
 */
export const compressMultipleImages = async (
  files: File[],
  options: CompressOptions = {}
): Promise<string[]> => {
  const promises = files.map(file => compressImage(file, options));
  return Promise.all(promises);
};

/**
 * 批量压缩图片并返回 File 对象数组
 * @param files 图片文件数组
 * @param options 压缩选项
 * @returns 压缩后的 File 对象数组
 */
export const compressMultipleImagesToFiles = async (
  files: File[],
  options: CompressOptions = {}
): Promise<File[]> => {
  const promises = files.map(file => compressImageToFile(file, options));
  return Promise.all(promises);
};

/**
 * 获取文件大小的友好显示
 * @param size 文件大小（字节）
 * @returns 格式化的大小字符串
 */
export const formatFileSize = (size: number): string => {
  if (size < 1024) return size + ' B';
  if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB';
  return (size / (1024 * 1024)).toFixed(2) + ' MB';
};

/**
 * 计算压缩率
 * @param originalSize 原始大小
 * @param compressedSize 压缩后大小
 * @returns 压缩率百分比
 */
export const calculateCompressionRatio = (
  originalSize: number,
  compressedSize: number
): number => {
  if (originalSize === 0) return 0;
  return Math.round((1 - compressedSize / originalSize) * 100);
};